import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Select from 'react-select';
import { createAffiliate } from '../../api/api_auth';
import AdminSidebar from '../../components/Admin/AdminSidebar';
import AdminHeader from '../../components/Admin/AdminHeader';

function CreateAffiliates() {
    const user = useSelector((state) => state.affiliate.auth);
    const navigate = useNavigate();
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        affiliate_role: 'affiliate',
        affiliate_type: null
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [identificationCode, setIdentificationCode] = useState('');

    const copyToClipboard = async (text) => {
        try {
            if (navigator && navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(text);
                return true;
            }
        } catch (e) {
            // fall back to legacy path
        }
        try {
            const textArea = document.createElement('textarea');
            textArea.value = text;
            textArea.setAttribute('readonly', '');
            textArea.style.position = 'absolute';
            textArea.style.left = '-9999px';
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            return true;
        } catch (e) {
            console.error('Clipboard copy failed:', e);
            return false;
        }
    };

    // Check if the user is an admin
    useEffect(() => {
        if (user?.affiliate_role !== "admin") {
            navigate("/login");
        }
    }, [user, navigate]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear messages when user starts typing
        setError('');
        setSuccess('');
        setIdentificationCode('');
    };

    const handleAffiliateTypeChange = (selectedOption) => {
        setFormData(prev => ({
            ...prev,
            affiliate_type: selectedOption ? selectedOption.value : null
        }));
        // Clear messages when user changes selection
        setError('');
        setSuccess('');
        setIdentificationCode('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validate affiliate_type is selected
        if (!formData.affiliate_type) {
            setError('Affiliate type är obligatoriskt');
            return;
        }

        setLoading(true);
        setError('');
        setSuccess('');
        setIdentificationCode('');

        try {
            const response = await createAffiliate(formData);
            
            if (response.data.identification_code) {
                setSuccess('Användaren skapades framgångsrikt!');
                setIdentificationCode(response.data.identification_code);
                setFormData({
                    name: '',
                    email: '',
                    phone: '',
                    affiliate_role: 'affiliate',
                    affiliate_type: null
                });
            } else {
                setError('Misslyckades med att skapa användaren');
            }
        } catch (error) {
            console.error('Error creating user:', error);
            if (error.response?.data?.error) {
                if (typeof error.response.data.error === 'object') {
                    // Handle validation errors
                    const errorMessages = Object.values(error.response.data.error).flat();
                    setError(errorMessages.join(', '));
                } else {
                    setError(error.response.data.error);
                }
            } else {
                setError('Misslyckades med att skapa användaren. Försök igen.');
            }
        } finally {
            setLoading(false);
        }
    };

    // Render nothing if user is not loaded yet or is not an admin
    if (!user || user?.affiliate_role !== "admin") {
        return (
            <div className="min-h-screen bg-gray-100 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-4 border-secondary-green border-t-transparent mx-auto"></div>
                    <p className="mt-6 text-gray-600 text-lg font-medium">Kontrollerar admin-behörigheter...</p>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-100 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-4 border-secondary-green border-t-transparent mx-auto"></div>
                    <p className="mt-6 text-gray-600 text-lg font-medium">Skapar användare...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100 flex">
            {/* Sidebar */}
            <AdminSidebar 
                collapsed={sidebarCollapsed} 
                onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} 
            />

            {/* Main Content */}
            <div className="flex-1 flex flex-col">
                {/* Header */}
                <AdminHeader 
                    onToggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)}
                    sidebarCollapsed={sidebarCollapsed}
                />

                {/* Content */}
                <div className="flex-1 p-6">
                    {/* Page Header */}
                    <div className="mb-8">
                        <div className="flex items-center justify-between">
                            <div>
                                <h1 className="text-3xl font-bold text-gray-900 mb-2">Create New User</h1>
                                <p className="text-gray-600">
                                    Add new users to the system with appropriate roles and permissions.
                                </p>
                            </div>
                            <Link 
                                to="/admin/users" 
                                className="bg-white hover:bg-gray-50 text-gray-700 font-semibold py-2 px-4 rounded-lg border border-gray-200 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                            >
                                ← Back to Users
                            </Link>
                        </div>
                    </div>

                    {/* Form Card */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    {/* Error and Success Messages */}
                    {error && (
                        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <img 
                                      src="/assets/icons/Radera.png" 
                                      alt="Error" 
                                      className="h-5 w-5 text-red-400"
                                    />
                                </div>
                                <div className="ml-3">
                                    <p className="text-sm text-red-800">{error}</p>
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {success && (
                        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <img 
                                      src="/assets/icons/Check.png" 
                                      alt="Success" 
                                      className="h-5 w-5 text-green-400"
                                    />
                                </div>
                                <div className="ml-3">
                                    <p className="text-sm text-green-800">{success}</p>
                                    {identificationCode && (
                                        <div className="mt-3 p-3 bg-white rounded-lg border border-green-200">
                                            <p className="text-sm font-semibold text-green-900 mb-2">Identifikationskod:</p>
                                            <div className="flex items-center space-x-2">
                                                <code className="px-3 py-2 bg-gray-100 rounded-lg text-lg font-mono text-gray-800">
                                                    {identificationCode}
                                                </code>
                                                <button
                                                    onClick={() => copyToClipboard(identificationCode)}
                                                    className="px-3 py-2 bg-secondary-green hover:bg-secondary-green/80 text-white rounded-lg transition-colors duration-200 text-sm"
                                                    title="Kopiera kod"
                                                >
                                                    <img 
                                                      src="/assets/icons/Check.png" 
                                                      alt="Copy" 
                                                      className="w-4 h-4"
                                                    />
                                                </button>
                                            </div>
                                            <p className="text-xs text-green-700 mt-2">
                                                Dela denna kod med användaren så att de kan logga in på systemet.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                                    Name
                                </label>
                                <input
                                    type="text"
                                    id="name"
                                    name="name"
                                    required
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="Enter full name"
                                />
                            </div>

                                <div>
                                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                                        Email
                                    </label>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        required
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="user@example.com"
                                    />
                                </div>

                                <div>
                                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                                        Phone
                                    </label>
                                    <input
                                        type="tel"
                                        id="phone"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleInputChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="+1 (555) 123-4567"
                                    />
                                </div>

                                <div>
                                    <label htmlFor="affiliate_role" className="block text-sm font-medium text-gray-700 mb-1">
                                        Role
                                    </label>
                                    <select
                                        id="affiliate_role"
                                        name="affiliate_role"
                                        value={formData.affiliate_role}
                                        onChange={handleInputChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        <option value="affiliate">Affiliate</option>
                                        <option value="admin">Admin</option>
                                    </select>
                                </div>

                                <div>
                                    <label htmlFor="affiliate_type" className="block text-sm font-medium text-gray-700 mb-1">
                                        Affiliate Type <span className="text-red-500">*</span>
                                    </label>
                                    <Select
                                        id="affiliate_type"
                                        name="affiliate_type"
                                        value={formData.affiliate_type ? {
                                            value: formData.affiliate_type,
                                            label: formData.affiliate_type.charAt(0).toUpperCase() + formData.affiliate_type.slice(1)
                                        } : null}
                                        onChange={handleAffiliateTypeChange}
                                        options={[
                                            { value: 'restaurant', label: 'Restaurant' },
                                            { value: 'municipality', label: 'Municipality' },
                                            { value: 'school', label: 'School' },
                                            { value: 'dietitian', label: 'Dietitian' }
                                        ]}
                                        placeholder="Select affiliate type..."
                                        isClearable={false}
                                        isSearchable={false}
                                        className="react-select-container"
                                        classNamePrefix="react-select"
                                        styles={{
                                            control: (base, state) => ({
                                                ...base,
                                                borderColor: !formData.affiliate_type && state.isFocused ? '#ef4444' : base.borderColor,
                                                boxShadow: !formData.affiliate_type && state.isFocused ? '0 0 0 1px #ef4444' : base.boxShadow,
                                            }),
                                        }}
                                    />
                                </div>
                        </div>

                            <div className="flex items-center justify-end space-x-4">
                                <button
                                    type="button"
                                    onClick={() => navigate('/admin/users')}
                                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {loading ? 'Creating...' : 'Create User'}
                                </button>
                            </div>
                        </form>
                    </div>

                    {/* Help Section */}
                    <div className="mt-8 bg-blue-50 rounded-xl p-6">
                        <div className="flex items-start">
                            <div className="flex-shrink-0">
                                <span className="text-2xl">ℹ️</span>
                            </div>
                            <div className="ml-3">
                                <h3 className="text-lg font-semibold text-blue-900 mb-2">Help</h3>
                                <p className="text-blue-800 mb-3">
                                    When you create a new user, the system will generate a unique identification code. 
                                    This code is used as the password when the user logs in for the first time.
                                </p>
                                <div className="space-y-2 text-sm text-blue-700">
                                    <p><strong>Roles:</strong></p>
                                    <ul className="list-disc list-inside space-y-1 ml-4">
                                        <li><strong>Admin:</strong> Full system access, can create other users</li>
                                        <li><strong>Affiliate:</strong> Standard user, can manage recipes and categories</li>
                                    </ul>
                                    <p className="mt-3"><strong>Important:</strong> Copy and securely share the identification code with the new user.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default CreateAffiliates;
